import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { useStockComparisonSnapshot } from "../Charts/hooks/useStockComparisonSnapshot";
import ComparisonSlot from "./ComparisonSlot";
import ComparisonPriceChart from "./ComparisonPriceChart";
import ComparisonMetricsTable from "./ComparisonMetricsTable";
import { MOBILE_TOPBAR_HEIGHT, useSidebar } from "../../../contexts/SidebarContext";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useMembership } from "../../../contexts/MembershipContext";

// Height of the two stacked slots on XS + 1px bottom border
const XS_TOPBAR_HEIGHT = 129;
// Height of the single-row topbar on sm+
const SM_TOPBAR_HEIGHT = 65;

interface ComparisonLayoutProps {
  leftTicker: string | null;
  rightTicker: string | null;
  onClearLeft: () => void;
  onClearRight: () => void;
  onSelectLeft: (ticker: string) => void;
  onSelectRight: (ticker: string) => void;
}

const COLOR_LEFT = "#22c55e";
const COLOR_RIGHT = "#3b82f6";

const ComparisonLayout = ({
  leftTicker,
  rightTicker,
  onClearLeft,
  onClearRight,
  onSelectLeft,
  onSelectRight,
}: ComparisonLayoutProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { isMobile, isTablet } = useBreakpoint();
  const { sidebarWidth } = useSidebar();
  const { isMembershipActive } = useMembership();
  const useMobileLayout = isMobile || isTablet;
  const topbarLeft = useMobileLayout ? 0 : sidebarWidth;
  const topbarWidth = useMobileLayout ? "100%" : `calc(100% - ${sidebarWidth}px)`;
  // The app top bar is MOBILE_TOPBAR_HEIGHT plus the device's notch inset
  // (native app; env() is 0 in ordinary browsers), so the fixed slots bar
  // must offset by both or it slides under the header.
  const topbarTop = useMobileLayout
    ? `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top, 0px))`
    : 0;
  const topbarSpacerHeight = isXs ? XS_TOPBAR_HEIGHT : SM_TOPBAR_HEIGHT;

  const { data: leftSnapshot } = useStockComparisonSnapshot(leftTicker);
  const { data: rightSnapshot } = useStockComparisonSnapshot(rightTicker);

  const border = isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0";
  const background = isDark ? "#1a1a1a" : "#ffffff";

  const showContent = !!leftSnapshot || !!rightSnapshot;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        backgroundColor: background,
      }}
    >
      {/* Topbar — always fixed; position:sticky is broken by ancestor overflowX:hidden */}
      <Box
        sx={{
          position: "fixed",
          top: topbarTop,
          left: topbarLeft,
          width: topbarWidth,
          zIndex: 100,
          backgroundColor: background,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          {/* Left slot */}
          <Box
            sx={{
              flex: 1,
              height: 64,
              borderRight: { sm: `1px solid ${border}` },
              borderBottom: { xs: `1px solid ${border}`, sm: "none" },
              overflow: "hidden",
            }}
          >
            <ComparisonSlot
              ticker={leftTicker}
              color={COLOR_LEFT}
              onSelect={onSelectLeft}
              onClear={onClearLeft}
              disabled={!isMembershipActive}
            />
          </Box>

          {/* Right slot */}
          <Box
            sx={{
              flex: 1,
              height: 64,
              overflow: "hidden",
            }}
          >
            <ComparisonSlot
              ticker={rightTicker}
              color={COLOR_RIGHT}
              placeholder="Search to compare..."
              onSelect={onSelectRight}
              onClear={onClearRight}
              disabled={!isMembershipActive}
            />
          </Box>
        </Box>
      </Box>

      {/* Spacer to push content below the fixed topbar */}
      <Box sx={{ height: topbarSpacerHeight }} />

      <Box sx={{ overflowX: "hidden" }}>
        {showContent && (
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 3 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2.5, sm: 3 },
              pb: { xs: 4, sm: 6 },
            }}
          >
            <ComparisonPriceChart
              ticker1={leftTicker ?? undefined}
              ticker2={rightTicker ?? undefined}
              color1={COLOR_LEFT}
              color2={COLOR_RIGHT}
            />
            <Box sx={{ px: { xs: 0.5, sm: 4, md: 10, lg: 16 } }}>
              <ComparisonMetricsTable snapshot1={leftSnapshot ?? null} snapshot2={rightSnapshot ?? null} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ComparisonLayout;
